import { useState, useEffect, lazy, Suspense } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import Navbar from '../components/Navbar';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Search, MapPin, Leaf, Wind, Brain, Hand, Sparkles, Video, Building, Filter, X, Globe, Target, Mic, Heart, Star, Award, Gem, Apple } from 'lucide-react';
import { COUNTRIES, FRANCE_REGIONS, getDepartmentsByRegion, getCountryName, getRegionName, getDepartmentName, getRegionsByCountry } from '../data/geography';
import { useLanguage } from '../contexts/LanguageContext';

// Lazy load map component for better performance
const PractitionerMap = lazy(() => import('../components/PractitionerMap'));

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Badge icon mapping
const BADGE_ICONS = {
  heart: Heart,
  award: Award,
  gem: Gem
};

// Badge color classes
const BADGE_COLORS = {
  pink: 'bg-pink-100 text-pink-600',
  blue: 'bg-blue-100 text-blue-600',
  amber: 'bg-amber-100 text-amber-700',
  gray: 'bg-gray-100 text-gray-600',
  yellow: 'bg-yellow-100 text-yellow-600',
  emerald: 'bg-emerald-100 text-emerald-600',
  red: 'bg-red-100 text-red-600'
};

const SPECIALTY_ICONS = {
  therapeute: Heart,
  naturopathe: Leaf,
  sophrologue: Wind,
  psychologue: Brain,
  osteopathe: Hand,
  hypnotherapeute: Sparkles,
  coach: Target,
  orthophoniste: Mic,
  nutritionniste: Apple,
};

const SearchPage = () => {
  const { t } = useLanguage();
  const [searchParams, setSearchParams] = useSearchParams();
  const [practitioners, setPractitioners] = useState([]);
  const [featuredPractitioners, setFeaturedPractitioners] = useState([]);
  const [specialties, setSpecialties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);

  const [filters, setFilters] = useState({
    specialty: searchParams.get('specialty') || '',
    country: searchParams.get('country') || '',
    region: searchParams.get('region') || '',
    department: searchParams.get('department') || '',
    city: searchParams.get('city') || '',
    consultationType: searchParams.get('type') || ''
  });

  // Get departments based on selected region
  const departments = filters.region ? getDepartmentsByRegion(filters.region) : [];

  // Check if any filter is active
  const hasActiveFilters = filters.specialty || filters.country || filters.region || filters.department || filters.city || filters.consultationType;

  useEffect(() => {
    const fetchSpecialties = async () => {
      try {
        const response = await axios.get(`${API}/specialties`);
        setSpecialties(response.data);
      } catch (error) {
        console.error('Error fetching specialties:', error);
      }
    };
    fetchSpecialties();
  }, []);

  // Fetch featured practitioners (random 5, only once on page load)
  useEffect(() => {
    const fetchFeatured = async () => {
      try {
        const response = await axios.get(`${API}/practitioners`);
        const allPractitioners = response.data;
        if (allPractitioners.length > 0) {
          // Shuffle and take 5
          const shuffled = [...allPractitioners].sort(() => Math.random() - 0.5);
          setFeaturedPractitioners(shuffled.slice(0, 5));
        }
      } catch (error) {
        console.error('Error fetching featured:', error);
      }
    };
    fetchFeatured();
  }, []);

  useEffect(() => {
    const fetchPractitioners = async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams();
        if (filters.specialty && filters.specialty !== 'all') params.set('specialty', filters.specialty);
        if (filters.country && filters.country !== 'all') params.set('country', filters.country);
        if (filters.region && filters.region !== 'all') params.set('region', filters.region);
        if (filters.department && filters.department !== 'all') params.set('department', filters.department);
        if (filters.city) params.set('city', filters.city);
        if (filters.consultationType && filters.consultationType !== 'all') params.set('consultation_type', filters.consultationType);

        const response = await axios.get(`${API}/practitioners?${params.toString()}`);
        setPractitioners(response.data);
      } catch (error) {
        console.error('Error fetching practitioners:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchPractitioners();
  }, [filters]);

  const handleFilterChange = (key, value) => {
    let newFilters = { ...filters, [key]: value };
    
    // Reset dependent filters when parent changes
    if (key === 'country') {
      if (value !== 'FR') {
        newFilters.region = '';
        newFilters.department = '';
      }
    }
    if (key === 'region') {
      newFilters.department = '';
    }
    
    setFilters(newFilters);
    
    // Update URL params
    const params = new URLSearchParams();
    if (newFilters.specialty && newFilters.specialty !== 'all') params.set('specialty', newFilters.specialty);
    if (newFilters.country && newFilters.country !== 'all') params.set('country', newFilters.country);
    if (newFilters.region && newFilters.region !== 'all') params.set('region', newFilters.region);
    if (newFilters.department && newFilters.department !== 'all') params.set('department', newFilters.department);
    if (newFilters.city) params.set('city', newFilters.city);
    if (newFilters.consultationType && newFilters.consultationType !== 'all') params.set('type', newFilters.consultationType);
    setSearchParams(params);
  };

  const clearFilters = () => {
    setFilters({ specialty: '', country: '', region: '', department: '', city: '', consultationType: '' });
    setSearchParams({});
  };

  // Format location display
  const formatLocation = (practitioner) => {
    const parts = [];
    if (practitioner.city) parts.push(practitioner.city);
    if (practitioner.department && practitioner.country === 'FR') {
      parts.push(`(${practitioner.department})`);
    }
    if (practitioner.country && practitioner.country !== 'FR') {
      parts.push(getCountryName(practitioner.country));
    }
    return parts.join(' ');
  };

  return (
    <div className="min-h-screen bg-background" data-testid="search-page">
      <Navbar />
      
      <div className="container-app py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="font-heading text-3xl md:text-4xl font-semibold text-foreground mb-2">
            {t('search.title')}
          </h1>
          <p className="text-muted-foreground">
            {practitioners.length} {t('search.practitionersAvailable')}
          </p>
        </div>

        {/* Featured Practitioners - Only shown when no filters active */}
        {!hasActiveFilters && featuredPractitioners.length > 0 && (
          <div className="mb-10 animate-fadeIn">
            <div className="flex items-center gap-2 mb-4">
              <Star className="w-5 h-5 text-amber-500 fill-amber-500" />
              <h2 className="font-heading text-xl font-medium">{t('search.featuredPractitioners') || 'Praticiens à découvrir'}</h2>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
              {featuredPractitioners.map((practitioner) => {
                const IconComponent = SPECIALTY_ICONS[practitioner.specialty] || Leaf;
                return (
                  <Link
                    key={`featured-${practitioner.id}`}
                    to={`/practitioner/${practitioner.id}`}
                    className="group bg-white rounded-xl border border-sand-200 overflow-hidden shadow-soft hover:shadow-md transition-all hover:-translate-y-1"
                    data-testid={`featured-practitioner-${practitioner.id}`}
                  >
                    <div className="aspect-square relative">
                      <img 
                        src={practitioner.photo_url 
                          ? (practitioner.photo_url.startsWith('/api/photos') || practitioner.photo_url.startsWith('/uploads')
                              ? `${process.env.REACT_APP_BACKEND_URL}${practitioner.photo_url}`
                              : practitioner.photo_url)
                          : `/images/default-practitioner.jpg`
                        }
                        alt={`Pr. ${practitioner.user?.first_name} ${practitioner.user?.last_name}`}
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute top-2 right-2 bg-white/90 backdrop-blur-sm rounded-full p-1.5 shadow-sm">
                        <IconComponent className="w-4 h-4 text-sage-600" />
                      </div>
                      {/* Badges overlay */}
                      {practitioner.badges && practitioner.badges.length > 0 && (
                        <div className="absolute bottom-2 left-2 flex items-center gap-1">
                          {practitioner.badges.map((badge) => {
                            const BadgeIcon = BADGE_ICONS[badge.icon] || Award;
                            const colorClass = BADGE_COLORS[badge.color] || BADGE_COLORS.blue;
                            return (
                              <div 
                                key={badge.id} 
                                className={`inline-flex items-center justify-center rounded-full w-6 h-6 ${colorClass} shadow-sm`}
                                title={`${badge.name} - ${badge.description}`}
                              >
                                <BadgeIcon className="w-3.5 h-3.5" />
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                    <div className="p-3">
                      <p className="font-semibold text-sm truncate text-gray-900">
                        Pr. {practitioner.user?.first_name} {practitioner.user?.last_name}
                      </p>
                      <p className="text-xs text-sage-600 font-medium truncate">
                        {t(`specialties.${practitioner.specialty}`) || practitioner.specialty}
                      </p>
                      {practitioner.city && (
                        <p className="text-xs text-muted-foreground truncate flex items-center gap-1 mt-1">
                          <MapPin className="w-3 h-3" />
                          {practitioner.city}
                        </p>
                      )}
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        )}

        {/* Mobile Filter Toggle */}
        <div className="md:hidden mb-4">
          <Button 
            variant="outline" 
            onClick={() => setShowFilters(!showFilters)}
            className="w-full justify-between"
            data-testid="mobile-filter-toggle"
          >
            <span className="flex items-center gap-2">
              <Filter className="w-4 h-4" />
              {t('search.filters')}
            </span>
            {hasActiveFilters && (
              <span className="bg-sage-500 text-white text-xs px-2 py-0.5 rounded-full">
                {t('search.clear')}
              </span>
            )}
          </Button>
        </div>

        <div className="flex flex-col md:flex-row gap-8">
          {/* Filters Sidebar */}
          <aside className={`w-full md:w-80 shrink-0 ${showFilters ? 'block' : 'hidden md:block'}`} data-testid="filters-sidebar">
            <div className="bg-white rounded-2xl p-6 border border-sand-200 shadow-soft sticky top-24">
              <div className="flex items-center justify-between mb-6">
                <h2 className="font-heading text-lg font-medium">{t('search.filters')}</h2>
                {hasActiveFilters && (
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={clearFilters}
                    className="text-muted-foreground hover:text-foreground"
                    data-testid="clear-filters"
                  >
                    <X className="w-4 h-4 mr-1" />
                    {t('search.clear')}
                  </Button>
                )}
              </div>

              <div className="space-y-5">
                {/* Specialty Filter */}
                <div>
                  <label className="form-label">{t('search.specialty')}</label>
                  <Select 
                    value={filters.specialty} 
                    onValueChange={(value) => handleFilterChange('specialty', value)}
                  >
                    <SelectTrigger className="w-full rounded-xl border-sand-200" data-testid="filter-specialty">
                      <SelectValue placeholder={t('search.allSpecialties')} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">{t('search.allSpecialties')}</SelectItem>
                      {specialties.map((spec) => (
                        <SelectItem key={spec.id} value={spec.id}>{t(`specialties.${spec.id}`) || spec.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Country Filter */}
                <div>
                  <label className="form-label flex items-center gap-2">
                    <Globe className="w-4 h-4" />
                    {t('search.country')}
                  </label>
                  <Select 
                    value={filters.country} 
                    onValueChange={(value) => handleFilterChange('country', value)}
                  >
                    <SelectTrigger className="w-full rounded-xl border-sand-200" data-testid="filter-country">
                      <SelectValue placeholder={t('search.allCountries')} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">{t('search.allCountries')}</SelectItem>
                      {COUNTRIES.map((country) => (
                        <SelectItem key={country.code} value={country.code}>
                          {country.flag} {country.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Region Filter (for all countries with regions) */}
                {filters.country && filters.country !== 'all' && getRegionsByCountry(filters.country).length > 0 && (
                  <div className="animate-fadeIn">
                    <label className="form-label">{t('search.region')}</label>
                    <Select 
                      value={filters.region} 
                      onValueChange={(value) => handleFilterChange('region', value)}
                    >
                      <SelectTrigger className="w-full rounded-xl border-sand-200" data-testid="filter-region">
                        <SelectValue placeholder={t('search.allRegions')} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">{t('search.allRegions')}</SelectItem>
                        {getRegionsByCountry(filters.country).map((region) => (
                          <SelectItem key={region.code} value={region.code}>{region.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {/* Department Filter (France only, when region selected) */}
                {filters.country === 'FR' && filters.region && filters.region !== 'all' && (
                  <div className="animate-fadeIn">
                    <label className="form-label">{t('search.department')}</label>
                    <Select 
                      value={filters.department} 
                      onValueChange={(value) => handleFilterChange('department', value)}
                    >
                      <SelectTrigger className="w-full rounded-xl border-sand-200" data-testid="filter-department">
                        <SelectValue placeholder={t('search.allDepartments')} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">{t('search.allDepartments')}</SelectItem>
                        {departments.map((dept) => (
                          <SelectItem key={dept.code} value={dept.code}>
                            {dept.code} - {dept.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {/* City Filter */}
                <div>
                  <label className="form-label">{t('search.city')}</label>
                  <div className="relative">
                    <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input 
                      type="text"
                      placeholder={t('search.cityPlaceholder')}
                      value={filters.city}
                      onChange={(e) => handleFilterChange('city', e.target.value)}
                      className="pl-10 rounded-xl border-sand-200"
                      data-testid="filter-city"
                    />
                  </div>
                </div>

                {/* Consultation Type Filter */}
                <div>
                  <label className="form-label">{t('search.consultationType')}</label>
                  <Select 
                    value={filters.consultationType} 
                    onValueChange={(value) => handleFilterChange('consultationType', value)}
                  >
                    <SelectTrigger className="w-full rounded-xl border-sand-200" data-testid="filter-consultation-type">
                      <SelectValue placeholder={t('search.allTypes')} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">{t('search.allTypes')}</SelectItem>
                      <SelectItem value="cabinet">{t('search.cabinet')}</SelectItem>
                      <SelectItem value="visio">{t('search.video')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Mobile Close Button */}
              <div className="md:hidden mt-6">
                <Button 
                  onClick={() => setShowFilters(false)}
                  className="w-full bg-sage-500 hover:bg-sage-600"
                >
                  {t('search.applyFilters')}
                </Button>
              </div>
            </div>
          </aside>

          {/* Results - Map Only */}
          <main className="flex-1" data-testid="search-results">
            {loading ? (
              <div className="flex items-center justify-center py-20">
                <div className="loader" />
              </div>
            ) : practitioners.length === 0 ? (
              <div className="empty-state bg-white rounded-2xl border border-sand-200">
                <Search className="w-12 h-12 mx-auto mb-4 text-muted-foreground/50" />
                <h3 className="font-heading text-xl font-medium mb-2">{t('search.noPractitionerFound')}</h3>
                <p className="text-muted-foreground mb-4">
                  {t('search.tryDifferentCriteria')}
                </p>
                {hasActiveFilters && (
                  <Button variant="outline" onClick={clearFilters}>
                    {t('search.clearFilters')}
                  </Button>
                )}
              </div>
            ) : (
              /* Map View - Always show map with all practitioners */
              <Suspense fallback={
                <div className="flex items-center justify-center py-20 bg-sand-50 rounded-2xl">
                  <div className="loader" />
                </div>
              }>
                <PractitionerMap 
                  practitioners={practitioners} 
                  height="calc(100vh - 300px)"
                />
              </Suspense>
            )}
          </main>
        </div>
      </div>
    </div>
  );
};

export default SearchPage;
